/**
 * Frontend companion to app/Support/ClassLabel.php.
 *
 * Every surface that composes a class label from grade + subject lists
 * must route through here so a name that renders one way in Class Sessions
 * cannot render differently on the Dashboard or Parent Portal.
 *
 * Rules mirror the backend:
 *   - Curator-first: if the class has a non-empty `name`, that value
 *     wins outright (single source: the backend model mutator coerces
 *     '' and whitespace-only to NULL; this frontend does the same
 *     belt-and-braces check).
 *   - Grades <= 3 AND subjects <= 3 → today's "grades subjects" concat
 *     (byte-identical to prior behaviour; ~85 normal classes unchanged).
 *   - Above either threshold → compact: "first, second +N more · first, second +N more"
 *   - Sort by inherent order (grades.order_number / subjects_tables.order_number)
 *     only when compacting, and only if the caller provides ranked lists.
 *
 * The backend already applies the curator-first rule when it serialises
 * `className`. Frontend consumers should prefer that pre-formatted field
 * when present:
 *     row.className ?? formatFromPrejoined(row.name, row.gradeName, row.subjectName)
 * These utils remain useful for surfaces where the FE composes labels
 * from raw arrays.
 */

const GRADE_CAP = 3;
const SUBJECT_CAP = 3;

const unique = (xs: (string | null | undefined)[]): string[] => {
  const seen = new Set<string>();
  const out: string[] = [];
  for (const x of xs) {
    const v = (x ?? "").toString();
    if (!v) continue;
    if (seen.has(v)) continue;
    seen.add(v);
    out.push(v);
  }
  return out;
};

const compact = (items: string[], cap: number): string => {
  const n = items.length;
  if (n === 0) return "";
  if (n <= cap) return items.join(", ");
  return `${items[0]}, ${items[1]} +${n - 2} more`;
};

export function formatClassLabel(
  curatorName: string | null | undefined,
  grades: (string | null | undefined)[],
  subjects: (string | null | undefined)[],
  gradesRanked?: (string | null | undefined)[],
  subjectsRanked?: (string | null | undefined)[],
): string {
  if (curatorName != null && String(curatorName).trim() !== "") {
    return String(curatorName).trim();
  }

  const g = unique(grades ?? []);
  const s = unique(subjects ?? []);

  if (g.length <= GRADE_CAP && s.length <= SUBJECT_CAP) {
    const left = g.join(", ");
    const right = s.join(", ");
    return `${left}${left && right ? " " : ""}${right}`.trim();
  }

  const gCompact = compact(
    (gradesRanked?.length ? unique(gradesRanked) : g),
    GRADE_CAP,
  );
  const sCompact = compact(
    (subjectsRanked?.length ? unique(subjectsRanked) : s),
    SUBJECT_CAP,
  );

  return [gCompact, sCompact].filter(Boolean).join(" · ");
}

/**
 * When the caller has pre-joined "a, b, c" strings from the backend
 * (Dashboard / ParentPortal2 shape) rather than arrays, split-then-format.
 * Same rules apply.
 */
export function formatFromPrejoined(
  curatorName: string | null | undefined,
  gradeName?: string | null,
  subjectName?: string | null,
): string {
  const g = (gradeName ?? "").split(",").map(s => s.trim()).filter(Boolean);
  const s = (subjectName ?? "").split(",").map(x => x.trim()).filter(Boolean);
  return formatClassLabel(curatorName, g, s);
}

/**
 * Live-preview helper for the class create/edit form. Renders exactly
 * what the class WOULD display as without a curator name, so the
 * curator sees what they are replacing.
 *
 * Usage:
 *   <TextField value={name} ... helperText={
 *     name.trim() === "" && (grades.length || subjects.length)
 *       ? `Will display as: ${previewCompactLabel(grades, subjects)}`
 *       : "Optional. Leave blank to auto-generate from grades and subjects."
 *   } />
 */
export function previewCompactLabel(
  grades: (string | null | undefined)[],
  subjects: (string | null | undefined)[],
): string {
  return formatClassLabel(null, grades, subjects);
}

// ─────────────────────────────────────────────────────────────────────
// Operations Dashboard class-summary (Aug 2026 redesign).
//
// `formatClassLabel` above is optimised for a compact class NAME —
// this helper is the read-side companion for operational rows (alerts /
// Live Now / Starting Soon / Today's Schedule) where a raw complex
// snapshot like "5,4,3,6,7" / "English and Math, Science, ..." would
// blow up an alert row. Rules per the redesign spec:
//   - simple  →  "Grade 5 — Math"
//   - <= 3 subjects for one grade →  "Grade 5 — Math, Science"
//   - otherwise                    →  "3 Grades · 5 Subjects" (isComplex=true)
// The `detail` field carries the enumerated version so consumers can
// surface it in a tooltip or a "View Subjects" drawer.
// ─────────────────────────────────────────────────────────────────────

const _tokenize = (s: string | null | undefined): string[] => {
  if (!s) return [];
  const raw = s.split(/,| and /i).map((x) => x.trim()).filter(Boolean);
  return Array.from(new Set(raw));
};

const _prefixGrade = (g: string): string =>
  (/^[0-9]+$/.test(g.trim()) ? `Grade ${g.trim()}` : g.trim());

export interface ClassSummary {
  compact: string;
  isComplex: boolean;
  detail: string;
}

export function summarizeClass(
  gradeRaw: string | null | undefined,
  subjectRaw: string | null | undefined,
): ClassSummary {
  const gradeStr = (gradeRaw || "").trim();
  const subjectStr = (subjectRaw || "").trim();
  const gradeTokens = _tokenize(gradeStr).map(_prefixGrade);
  const subjectTokens = _tokenize(subjectStr);
  const gCount = gradeTokens.length;
  const sCount = subjectTokens.length;

  if (gCount === 0 && sCount === 0) {
    return { compact: "Class", isComplex: false, detail: "" };
  }
  if (gCount <= 1 && sCount <= 1) {
    const g = gradeTokens[0] || _prefixGrade(gradeStr);
    const s = subjectTokens[0] || subjectStr;
    const compact = [g, s].filter(Boolean).join(" — ") || "Class";
    return { compact, isComplex: false, detail: compact };
  }
  if (gCount <= 1 && sCount > 1 && sCount <= 3) {
    const g = gradeTokens[0] || _prefixGrade(gradeStr);
    const label = `${g} — ${subjectTokens.join(", ")}`;
    return { compact: label, isComplex: false, detail: label };
  }
  return {
    compact: `${gCount || 1} Grade${gCount === 1 ? "" : "s"} · ${sCount || 1} Subject${sCount === 1 ? "" : "s"}`,
    isComplex: true,
    detail: [gradeTokens.join(", "), subjectTokens.join(", ")].filter(Boolean).join(" — "),
  };
}
